import { useTranslation } from 'react-i18next';
import { GuideJourneyScreen } from './GuideJourneyScreen';

/**
 * The screen a guide lands on the moment they apply.
 *
 * It is the only page in the flow where the applicant has nothing to do, so it
 * is built to answer one question — "what happens now?" — rather than to push
 * an action. A first-time applicant gets no sign-in button at all: they have
 * just submitted, there is nothing yet to check, and offering it invites a
 * pointless round trip. Someone who was told they had already applied does get
 * it, because for them checking the status is the whole point.
 *
 * It is the same `GuideJourneyScreen` the authenticated status page renders,
 * so signing in later shows a continuation rather than a different site.
 */

interface GuideApplicationSubmittedProps {
  /** They already had an application; the screen becomes a sign-in prompt. */
  alreadyApplied: boolean;
  onSignIn: () => void;
  onBackHome: () => void;
}

export function GuideApplicationSubmitted({
  alreadyApplied,
  onSignIn,
  onBackHome,
}: GuideApplicationSubmittedProps) {
  const { t } = useTranslation('guideProfile');
  const variant = alreadyApplied ? 'alreadyApplied' : 'applied';
  const backHome = { label: t('submitted.backHome'), onClick: onBackHome };

  return (
    <GuideJourneyScreen
      overlayNavbar
      tone="success"
      stage={alreadyApplied ? 'verification' : 'application'}
      title={t(`submitted.${variant}.title`)}
      body={t(`submitted.${variant}.body`)}
      primaryAction={
        alreadyApplied
          ? {
              label: t('submitted.signInToCheckStatus'),
              onClick: onSignIn,
              intent: 'primary-gold',
            }
          : backHome
      }
      secondaryAction={alreadyApplied ? backHome : undefined}
    />
  );
}
